import { createQuery, createUrl } from '../helpers'
import {
  DEFAULT_REQUEST_OPTIONS,
  DEFAULT_LIST_ITEM_REQUEST_OPTIONS
} from '../config'
import type { ApiClient } from '../../index'
import type { RequestBody } from '../../types'
import type {
  DeepPartial,
  Id,
  Query,
  RequestOptionsNoQuery,
  PathOptions,
  Path,
  ItemOptions
} from '../types'

/**
 * **Provides CRUD methods for manipulating an entity in a list**
 *
 * Also supports a dynamic path via ```pathOptions```
 */
export const listItemWrapper = <
  T,
  UpdateBody extends RequestBody = RequestBody,
  CreateBody extends RequestBody = RequestBody,
  PathO extends PathOptions = PathOptions
>(
  apiClient: ApiClient,
  receivedOptions: ItemOptions<PathO>
) => {
  const baseRequestOptions = (() => {
    const mergedOptions = Object.assign(
      {},
      DEFAULT_REQUEST_OPTIONS,
      DEFAULT_LIST_ITEM_REQUEST_OPTIONS,
      receivedOptions.requestOptions
    )

    delete mergedOptions.query
    return mergedOptions as Omit<typeof mergedOptions, 'query'>
  })()

  const baseQuery = Object.assign(
    {},
    DEFAULT_REQUEST_OPTIONS.query,
    DEFAULT_LIST_ITEM_REQUEST_OPTIONS.query,
    receivedOptions.requestOptions?.query
  )

  const getUrl = (pathOptions?: PathO, requestQuery: Query = {}, id?: Id) => {
    const url = createUrl(
      receivedOptions.path as Path,
      requestQuery,
      pathOptions ?? {}
    )

    return url + (id === undefined ? '' : '/' + id)
  }

  const getQuery = (requestQuery: Query) => {
    const mergedQuery = Object.assign({}, baseQuery, requestQuery)

    return createQuery(mergedQuery)
  }

  return {
    getItem: <U extends DeepPartial<T> = T>(
      id: Id,
      requestQuery: Query = {},
      pathOptions?: PathO,
      requestOptions: RequestOptionsNoQuery = {}
    ) => {
      const options = Object.assign({}, baseRequestOptions, requestOptions)

      return apiClient.get<U>(
        getUrl(pathOptions, requestQuery, id),
        { query: getQuery(requestQuery) },
        { cache: options.cache }
      )
    },

    updateItem: <U extends DeepPartial<T> = T>({
      id,
      updates,
      requestQuery = {},
      pathOptions
    }: {
      id: Id
      updates: UpdateBody
      requestQuery?: Query
      pathOptions?: PathO
    }) =>
      apiClient.put<U>(getUrl(pathOptions, requestQuery, id), {
        body: updates,
        query: getQuery(requestQuery)
      }),

    createItem: <U extends DeepPartial<T> = T>({
      body,
      requestQuery = {},
      pathOptions
    }: {
      body: CreateBody
      requestQuery?: Query
      pathOptions?: PathO
    }) =>
      apiClient.post<U>(getUrl(pathOptions, requestQuery), {
        body,
        query: getQuery(requestQuery)
      }),

    removeItem: <R = any>(id: Id, pathOptions?: PathO) =>
      apiClient.delete<R>(getUrl(pathOptions, undefined, id))
  }
}

export type ListItemWrapperPublicInterface = ReturnType<typeof listItemWrapper>
